import { useEffect, useState } from "react";
import ThemeToggle from "../../components/ThemeToggle";
import { BrandMark, useSiteBrand, shortChurchName } from "./brand";
import { CalendarIcon, HomeIcon, MessageIcon } from "./icons";
import { fmtDate, fmtTime } from "./utils";

export const PublicHeader = ({ onNav, prayerLink, tagline, churchName, nextService, whatsapp, sections = {} }) => {
  const [open, setOpen] = useState(false);
  const siteBrand = useSiteBrand();
  const brand = shortChurchName(churchName, siteBrand);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Menu curto: só as secções principais, e só as que existem nesta página
  // (o resto continua acessível no rodapé). O primeiro é sempre «Início».
  const items = [
    { label: "Início", href: "#topo" },
    { label: "Cultos", href: "#cultos" },
    ...(sections.about ? [{ label: "Quem somos", href: "#sobre" }] : []),
    ...(sections.leadership ? [{ label: "Liderança", href: "#lideranca" }] : []),
    ...(sections.announcements ? [{ label: "Anúncios", href: "#anuncios" }] : []),
    ...(sections.gallery ? [{ label: "Galeria", href: "#galeria" }] : []),
    { label: "Contactos", href: "#contactos" },
  ];

  return (
    <header className="public-site-header fixed md:sticky md:top-0 inset-x-0 top-0 z-30 bg-white dark:bg-slate-900/85 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700/70 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
      <div className="public-site-header__inner max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="public-site-header__top flex items-center justify-between gap-3">
          <div className="public-site-header__brand flex items-center gap-3 min-w-0">
            <BrandMark brand={siteBrand} className="h-10 w-10 shrink-0 rounded-2xl shadow-sm" />
            <div className="leading-tight min-w-0 max-w-[180px] lg:max-w-[240px]">
              <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base truncate">{brand}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{tagline || (siteBrand.isDefault ? "Comunidade viva em Maputo" : "")}</div>
            </div>
          </div>

          <div className="public-site-header__actions flex items-center gap-3 shrink-0">
          <div className="hidden sm:block">
            <ThemeToggle />
          </div>
          <a
            href={prayerLink || "#"}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-sm hover:bg-slate-800 transition whitespace-nowrap"
          >
            Pedir oração
          </a>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl border border-white dark:border-slate-900/70 bg-white dark:bg-slate-900/60 shadow-sm"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="public-mobile-menu"
          >
            <span className="text-slate-700 dark:text-slate-300 text-lg">{open ? "✕" : "☰"}</span>
          </button>
          </div>
        </div>

        <nav className="public-site-header__nav public-desktop-nav hidden md:flex" aria-label="Navegação principal">
          <div className="public-site-header__nav-list flex items-center gap-1 rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-900/80 p-1 shadow-sm">
            {items.map((item, index) => (
              <a
                key={item.href}
                href={item.href}
                aria-label={item.href === "#topo" ? "Home — Início" : item.label}
                onClick={(event) => {
                  event.preventDefault();
                  onNav(item.href);
                }}
                className={`inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-xl px-2.5 py-2 text-sm font-semibold transition ${
                  index === 0
                    ? "bg-slate-900 text-white shadow-sm hover:bg-indigo-700"
                    : "text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-900 hover:text-indigo-700 dark:hover:text-indigo-300"
                }`}
              >
                {index === 0 ? <HomeIcon className="h-4 w-4" /> : null}
                <span>{item.label}</span>
              </a>
            ))}
          </div>
        </nav>
      </div>

      {open ? (
        <div id="public-mobile-menu" className="md:hidden border-t border-white dark:border-slate-900/60 bg-white dark:bg-slate-900/90 backdrop-blur-xl" role="dialog" aria-label="Navegação rápida">
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2 pb-2">
              <a
                href="#cultos"
                onClick={() => {
                  setOpen(false);
                  onNav("#cultos");
                }}
                className="flex min-h-16 flex-col justify-between rounded-2xl border border-indigo-100 dark:border-indigo-900/50 bg-indigo-50/80 dark:bg-indigo-950/40 px-3 py-2.5 text-left text-indigo-900 dark:text-indigo-200 shadow-sm"
              >
                <CalendarIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                <span className="text-xs font-bold">Próximo culto</span>
                <span className="truncate text-[11px] text-indigo-700 dark:text-indigo-300">
                  {nextService ? `${fmtDate(nextService.date)} · ${fmtTime(nextService.time)}` : "Ver horários"}
                </span>
              </a>
              {whatsapp ? (
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex min-h-16 flex-col justify-between rounded-2xl border border-emerald-100 dark:border-emerald-900/50 bg-emerald-50/80 dark:bg-emerald-950/40 px-3 py-2.5 text-left text-emerald-900 dark:text-emerald-300 shadow-sm"
                >
                  <MessageIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
                  <span className="text-xs font-bold">WhatsApp</span>
                  <span className="text-[11px] text-emerald-700 dark:text-emerald-300">Falar connosco</span>
                </a>
              ) : null}
            </div>
            {items.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => {
                  setOpen(false);
                  onNav(item.href);
                }}
                className="px-3 py-2 rounded-xl hover:bg-white dark:hover:bg-slate-900/70 text-slate-700 dark:text-slate-300 font-semibold"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-2 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-600 dark:text-slate-400 truncate">{brand}</div>
              <div className="flex items-center gap-2 shrink-0">
                <ThemeToggle labelClassName="hidden" />
                <a
                  href={prayerLink || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold shrink-0"
                >
                  Pedir oração
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
};
